// Who sings when, before the cover is made (after RaceCut's WhoSings), as
// the content of its own sheet in /create: two choices ("Auto pick",
// the default, leaves it to the jammer's swap detection; "Choose myself"),
// then the song as one tall bar, each part coloured by the voice that sings
// it. Drag a border to move a swap; tap a part to select it, and its panel
// under the bar plays it, gives it to another voice, splits or removes it.
//
// The stretches go to POST /api/v2/conversion as `voice_times` (keyed by
// voice id; see chosenVoiceTimes there). They need the song's audio here, so
// only songs with a playable file (uploads, recordings, the library) can be
// edited; a link or a generated song is left to Jammable.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play, Scissors, Sparkles, Trash2, Users } from "lucide-react";
import { Spinner } from "@jammable/ui-components/Loader";
import { VoiceImage } from "./StudioChrome";

/* eslint-disable @typescript-eslint/no-explicit-any */

export type Stretch = { voice: string; start: number; end: number };

/** Shorter than this and the jammer has nothing to sing (chosenVoiceTimes). */
export const SHORTEST_STRETCH_SECS = 2.5;
export const VOICE_COLORS = [
  "#782DC8",
  "#FF4D8D",
  "#1FB8D6",
  "#F59E0B",
  "#22C55E",
  "#3B5BFF",
];

/** Neighbours with the same voice become one stretch. */
function fused(stretches: Stretch[]): Stretch[] {
  const out: Stretch[] = [];
  for (const s of stretches) {
    const last = out[out.length - 1];
    if (last && last.voice === s.voice) last.end = s.end;
    else out.push({ ...s });
  }
  return out;
}

/** A first draft to edit: the voices take turns, a few parts each. */
export function draftStretches(voiceIds: string[], secs: number): Stretch[] {
  const n = Math.max(1, voiceIds.length);
  const parts = Math.max(n, Math.min(n * 3, Math.floor(secs / 12)));
  const len = secs / parts;
  return fused(
    Array.from({ length: parts }, (_, k) => ({
      voice: voiceIds[k % n],
      start: k * len,
      end: k === parts - 1 ? secs : (k + 1) * len,
    })),
  );
}

const clock = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

/** The song, decoded once: what plays and what the bar draws. */
function useSong(url: string | null) {
  const [buffer, setBuffer] = useState<AudioBuffer | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!url) return;
    let alive = true;
    setBuffer(null);
    setFailed(false);
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(String(res.status));
        const ctx = new AudioContext();
        const decoded = await ctx.decodeAudioData(await res.arrayBuffer());
        ctx.close().catch(() => {});
        if (alive) setBuffer(decoded);
      } catch {
        if (alive) setFailed(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { buffer, failed };
}

/** Plays a stretch of the song (or from a point); one source at a time. */
function usePlayer(buffer: AudioBuffer | null) {
  const ctx = useRef<AudioContext | null>(null);
  const source = useRef<AudioBufferSourceNode | null>(null);
  const raf = useRef(0);
  const [at, setAt] = useState<number | null>(null);
  const stop = () => {
    cancelAnimationFrame(raf.current);
    try {
      source.current?.stop();
    } catch {
      /* already stopped */
    }
    source.current = null;
    setAt(null);
  };
  useEffect(
    () => () => {
      stop();
      ctx.current?.close().catch(() => {});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const play = async (from: number, until?: number) => {
    if (!buffer) return;
    stop();
    const audio = (ctx.current ??= new AudioContext());
    if (audio.state === "suspended") await audio.resume();
    const src = audio.createBufferSource();
    src.buffer = buffer;
    src.connect(audio.destination);
    const length = (until ?? buffer.duration) - from;
    src.start(0, from, Math.max(0.05, length));
    source.current = src;
    const began = audio.currentTime;
    src.onended = () => {
      if (source.current === src) stop();
    };
    const tick = () => {
      if (source.current !== src) return;
      setAt(from + (audio.currentTime - began));
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };
  return { at, playing: at !== null, play, stop };
}

/** The song's loudness as bars, for the bar's background. */
function peaksOf(buffer: AudioBuffer, n = 160): number[] {
  const data = buffer.getChannelData(0);
  const per = Math.floor(data.length / n);
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    let max = 0;
    for (let k = i * per; k < (i + 1) * per; k += 64)
      max = Math.max(max, Math.abs(data[k]));
    out.push(max);
  }
  const top = Math.max(...out, 0.001);
  return out.map((v) => v / top);
}

/** One line for /create's row: what's chosen. */
export function whoSingsSummary(value: Stretch[] | null): string {
  if (!value || value.length === 0) return "Auto pick";
  return `You chose · ${value.length} ${value.length === 1 ? "part" : "parts"}`;
}

// The two ways, as one switch: a thumb that slides to the chosen half.
const ModeSwitch = ({
  manual,
  canChoose,
  onChoose,
}: {
  manual: boolean;
  canChoose: boolean;
  onChoose: (manual: boolean) => void;
}) => (
  <div
    role="radiogroup"
    aria-label="Who sings when"
    className="tw-relative tw-grid tw-h-14 tw-grid-cols-2 tw-rounded-full tw-bg-jam-lav tw-p-1.5"
  >
    <span
      aria-hidden
      className="tw-absolute tw-inset-y-1.5 tw-left-1.5 tw-w-[calc(50%-6px)] tw-rounded-full tw-bg-white tw-shadow-[0_2px_10px_-2px_rgba(46,15,82,0.25)] tw-transition-transform tw-duration-300 tw-ease-[cubic-bezier(.3,1.3,.5,1)]"
      style={{ transform: manual ? "translateX(100%)" : undefined }}
    />
    {(
      [
        [
          false,
          "Auto pick",
          <Sparkles key="a" size={17} strokeWidth={2.5} />,
        ],
        [true, "Choose myself", <Users key="b" size={17} strokeWidth={2.5} />],
      ] as const
    ).map(([value, label, icon]) => {
      const on = manual === value;
      return (
        <button
          key={label}
          type="button"
          role="radio"
          aria-checked={on}
          disabled={value && !canChoose}
          onClick={() => onChoose(value)}
          className={`tw-relative tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-transparent tw-text-[15px] tw-font-extrabold tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-40 ${on ? "tw-text-jam-ink" : "tw-text-jam-muted hover:tw-text-jam-ink"}`}
        >
          <span className={on ? "tw-text-jam-purple" : ""}>{icon}</span>
          {label}
        </button>
      );
    })}
  </div>
);

const Face = ({
  voice,
  color,
  size,
}: {
  voice: any;
  color: string;
  size: number;
}) => (
  <span
    className="tw-relative tw-block tw-shrink-0 tw-overflow-hidden tw-rounded-full tw-bg-jam-line"
    style={{ width: size, height: size, boxShadow: `0 0 0 2.5px ${color}` }}
  >
    <VoiceImage
      model={voice}
      fill
      sizes={`${size * 2}px`}
      className="tw-object-cover"
    />
  </span>
);

export default function WhoSings({
  voices,
  songUrl,
  value,
  onChange,
}: {
  voices: any[];
  /** A playable file for the song, or null (a link, a generated song). */
  songUrl: string | null;
  /** null: Auto pick. */
  value: Stretch[] | null;
  onChange: (v: Stretch[] | null) => void;
}) {
  const manual = value !== null;
  const { buffer, failed } = useSong(manual ? songUrl : null);
  const player = usePlayer(buffer);
  const bar = useRef<HTMLDivElement | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [removing, setRemoving] = useState(false);
  const [drag, setDrag] = useState<{ border: number; secs: number } | null>(
    null,
  );
  const [refusal, setRefusal] = useState<string | null>(null);
  const secs = buffer?.duration ?? 0;
  const peaks = useMemo(() => (buffer ? peaksOf(buffer) : []), [buffer]);
  const colorOf = (id: string) =>
    VOICE_COLORS[
      Math.max(
        0,
        voices.findIndex((v) => v._id === id),
      ) % VOICE_COLORS.length
    ];
  const voiceOf = (id: string) => voices.find((v) => v._id === id);

  // The first draft, once the song's length is known.
  useEffect(() => {
    if (manual && buffer && value && value.length === 0)
      onChange(
        draftStretches(
          voices.map((v) => v._id),
          buffer.duration,
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual, buffer]);
  useEffect(() => {
    if (!refusal) return;
    const t = setTimeout(() => setRefusal(null), 2600);
    return () => clearTimeout(t);
  }, [refusal]);
  useEffect(() => () => player.stop(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const stretches = value ?? [];
  const propose = (next: Stretch[], select: number | null = null) => {
    const f = fused(next).filter((s) => s.end > s.start);
    if (f.some((s) => s.end - s.start < SHORTEST_STRETCH_SECS - 1e-6)) {
      setRefusal(`Each part needs at least ${SHORTEST_STRETCH_SECS} seconds.`);
      return false;
    }
    onChange(f);
    setRemoving(false);
    setSelected(select !== null && select < f.length ? select : null);
    return true;
  };

  const secsAt = (clientX: number) => {
    const r = bar.current!.getBoundingClientRect();
    return Math.max(0, Math.min(secs, ((clientX - r.left) / r.width) * secs));
  };
  const shown = drag
    ? stretches.map((s, i) =>
        i === drag.border
          ? { ...s, end: drag.secs }
          : i === drag.border + 1
            ? { ...s, start: drag.secs }
            : s,
      )
    : stretches;
  const pct = (s: number) => `${(s / Math.max(0.01, secs)) * 100}%`;
  const tickEvery = secs > 240 ? 60 : secs > 90 ? 30 : 15;

  const give = (index: number, voice: string) => {
    // Where the part ends up after fusing with same-voice neighbours.
    const merged = fused(
      stretches.map((s, i) => (i === index ? { ...s, voice } : s)),
    );
    const at = stretches[index].start;
    propose(
      stretches.map((s, i) => (i === index ? { ...s, voice } : s)),
      merged.findIndex((s) => s.start <= at && at < s.end),
    );
  };
  const split = (index: number) => {
    const s = stretches[index];
    const mid = Math.round(((s.start + s.end) / 2) * 10) / 10;
    const other = voices.find((v) => v._id !== s.voice)?._id ?? s.voice;
    propose(
      [
        ...stretches.slice(0, index),
        { ...s, end: mid },
        { voice: other, start: mid, end: s.end },
        ...stretches.slice(index + 1),
      ],
      index + 1,
    );
  };
  const remove = (index: number, by: "left" | "right") => {
    const gone = stretches[index];
    propose(
      stretches
        .filter((_, i) => i !== index)
        .map((s, i) =>
          by === "left" && i === index - 1
            ? { ...s, end: gone.end }
            : by === "right" && i === index
              ? { ...s, start: gone.start }
              : s,
        ),
    );
  };

  const choose = (on: boolean) => {
    player.stop();
    setSelected(null);
    setRemoving(false);
    if (on !== manual) onChange(on ? [] : null);
  };

  // Each voice's share of the song, for the legend.
  const share = (id: string) =>
    stretches
      .filter((s) => s.voice === id)
      .reduce((n, s) => n + (s.end - s.start), 0);

  const part = selected !== null ? stretches[selected] : null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <ModeSwitch manual={manual} canChoose={!!songUrl} onChoose={choose} />

      {/* What "Auto pick" means, with who's in it. */}
      {!manual && (
        <div className="tw-flex tw-items-center tw-gap-3.5 tw-rounded-[22px] tw-border-2 tw-border-dashed tw-border-jam-line tw-p-4">
          <div className="tw-flex tw-shrink-0 tw-pl-1">
            {voices.slice(0, 4).map((v, i) => (
              <span key={v._id} className={i ? "tw--ml-2.5" : ""}>
                <Face voice={v} color={colorOf(v._id)} size={36} />
              </span>
            ))}
          </div>
          <div className="tw-min-w-0">
            <div className="tw-text-[14px] tw-font-extrabold tw-text-jam-ink">
              The voices take turns wherever the singing changes.
            </div>
            <div className="tw-mt-0.5 tw-text-[12px] tw-font-semibold tw-text-jam-muted">
              {songUrl
                ? "Want a say? Choose myself lets you pick who sings each part."
                : "Choosing yourself needs an upload, a recording or a library song."}
            </div>
          </div>
        </div>
      )}

      {manual && !buffer && !failed && (
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-[22px] tw-bg-jam-lav tw-py-10 tw-text-[14px] tw-font-bold tw-text-jam-muted">
          <Spinner size={4} className="tw-border-3 tw-border-jam-purple" />
          Loading the song…
        </div>
      )}
      {manual && failed && (
        <div className="tw-rounded-[22px] tw-bg-jam-lav tw-p-4 tw-text-[14px] tw-font-semibold tw-text-jam-muted">
          We couldn&apos;t play this song here, so Jammable will decide.
        </div>
      )}

      {manual && buffer && stretches.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-4">
          {/* Who's in it, and how much each sings. */}
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {voices.map((v) => (
              <span
                key={v._id}
                className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-jam-lav tw-py-1 tw-pl-1 tw-pr-3"
              >
                <Face voice={v} color={colorOf(v._id)} size={26} />
                <span className="tw-text-[13px] tw-font-extrabold tw-text-jam-ink">
                  {v.artistname}
                </span>
                <span className="tw-text-[12px] tw-font-bold tw-tabular-nums tw-text-jam-muted">
                  {clock(share(v._id))}
                </span>
              </span>
            ))}
          </div>

          {/* The song: parts, waveform, swaps, playhead, ruler, faces. */}
          <div className="tw-flex tw-items-start tw-gap-3">
            <button
              type="button"
              onClick={() => (player.playing ? player.stop() : player.play(0))}
              aria-label={player.playing ? "Pause" : "Play the song"}
              className="tw-mt-5 tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-white"
            >
              {player.playing ? (
                <Pause size={20} fill="currentColor" strokeWidth={0} />
              ) : (
                <Play
                  size={20}
                  fill="currentColor"
                  strokeWidth={0}
                  className="tw-ml-0.5"
                />
              )}
            </button>
            <div className="tw-min-w-0 tw-flex-1">
              <div
                ref={bar}
                className="tw-relative tw-h-[88px] tw-select-none tw-rounded-[18px] tw-bg-jam-lav"
              >
                {shown.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setRemoving(false);
                      setSelected(i);
                      player.play(s.start, s.end);
                    }}
                    title={`${voiceOf(s.voice)?.artistname ?? ""} · ${clock(s.start)}–${clock(s.end)}`}
                    aria-label={`${voiceOf(s.voice)?.artistname ?? ""}, ${clock(s.start)} to ${clock(s.end)}`}
                    aria-pressed={selected === i}
                    className="tw-absolute tw-inset-y-1.5 tw-cursor-pointer tw-border-0 tw-p-0 tw-transition-[filter,box-shadow]"
                    style={{
                      left: `calc(${pct(s.start)} + 1.5px)`,
                      width: `calc(${pct(s.end - s.start)} - 3px)`,
                      background: colorOf(s.voice),
                      borderRadius: 12,
                      filter:
                        selected !== null && selected !== i
                          ? "saturate(.55) brightness(1.08)"
                          : undefined,
                      boxShadow:
                        selected === i ? "0 0 0 3px #2E0F52" : undefined,
                    }}
                  />
                ))}
                {/* The song's loudness over the parts. */}
                <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-gap-px tw-overflow-hidden tw-rounded-[18px] tw-px-1.5">
                  {peaks.map((p, i) => (
                    <i
                      key={i}
                      className="tw-block tw-flex-1 tw-rounded-full tw-bg-white/45"
                      style={{ height: `${10 + p * 70}%` }}
                    />
                  ))}
                </div>
                {/* Swaps: drag to move. */}
                {shown.slice(0, -1).map((s, i) => (
                  <span
                    key={`b${i}`}
                    role="slider"
                    aria-label="Move where the voices swap"
                    aria-valuenow={Math.round(s.end)}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.currentTarget.setPointerCapture(e.pointerId);
                      setDrag({ border: i, secs: s.end });
                    }}
                    onPointerMove={(e) => {
                      if (!drag || drag.border !== i) return;
                      const lo = stretches[i].start + SHORTEST_STRETCH_SECS;
                      const hi = stretches[i + 1].end - SHORTEST_STRETCH_SECS;
                      const t = Math.round(secsAt(e.clientX) * 10) / 10;
                      setDrag({
                        border: i,
                        secs: Math.min(hi, Math.max(lo, t)),
                      });
                    }}
                    onPointerUp={() => {
                      if (!drag) return;
                      const { border, secs: t } = drag;
                      setDrag(null);
                      propose(
                        stretches.map((x, k) =>
                          k === border
                            ? { ...x, end: t }
                            : k === border + 1
                              ? { ...x, start: t }
                              : x,
                        ),
                        selected,
                      );
                    }}
                    onPointerCancel={() => setDrag(null)}
                    className="tw-absolute tw-inset-y-0 tw-z-10 tw-flex tw-w-6 tw--translate-x-1/2 tw-cursor-ew-resize tw-touch-none tw-items-center tw-justify-center"
                    style={{ left: pct(s.end) }}
                  >
                    <i
                      className={`tw-block tw-h-12 tw-w-2 tw-rounded-full tw-bg-white tw-shadow-[0_0_0_2px_rgba(46,15,82,0.9)] tw-transition-transform ${drag?.border === i ? "tw-scale-110" : ""}`}
                    />
                  </span>
                ))}
                {drag && (
                  <span
                    className="tw-pointer-events-none tw-absolute tw--top-8 tw-z-20 tw--translate-x-1/2 tw-rounded-full tw-bg-jam-ink tw-px-2 tw-py-1 tw-text-[11px] tw-font-extrabold tw-tabular-nums tw-text-white"
                    style={{ left: pct(drag.secs) }}
                  >
                    {clock(drag.secs)}
                  </span>
                )}
                {player.at !== null && (
                  <span
                    className="tw-pointer-events-none tw-absolute tw--inset-y-1 tw-z-20 tw-w-0.5 tw-rounded-full tw-bg-jam-ink"
                    style={{ left: pct(Math.min(secs, player.at)) }}
                  />
                )}
              </div>

              {/* Time ruler. */}
              <div className="tw-relative tw-mt-1 tw-h-4">
                {Array.from(
                  { length: Math.floor(secs / tickEvery) + 1 },
                  (_, k) => k * tickEvery,
                ).map((t) => (
                  <span
                    key={t}
                    className="tw-absolute tw-text-[10px] tw-font-bold tw-tabular-nums tw-text-jam-muted"
                    style={{
                      left: pct(t),
                      transform: t === 0 ? undefined : "translateX(-50%)",
                    }}
                  >
                    {clock(t)}
                  </span>
                ))}
              </div>

              {/* Who sings each part, under it. */}
              <div className="tw-relative tw-mt-1 tw-h-8">
                {shown.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setRemoving(false);
                      setSelected(i);
                    }}
                    title={voiceOf(s.voice)?.artistname}
                    className="tw-absolute tw-top-0 tw--translate-x-1/2 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
                    style={{ left: pct((s.start + s.end) / 2) }}
                  >
                    <Face
                      voice={voiceOf(s.voice)}
                      color={colorOf(s.voice)}
                      size={28}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* The selected part. */}
          {part ? (
            <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[22px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-4">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <div className="tw-flex tw-items-center tw-gap-2.5">
                  <Face
                    voice={voiceOf(part.voice)}
                    color={colorOf(part.voice)}
                    size={36}
                  />
                  <div>
                    <div className="tw-text-[15px] tw-font-extrabold tw-text-jam-ink">
                      {voiceOf(part.voice)?.artistname}
                    </div>
                    <div className="tw-text-[12px] tw-font-bold tw-tabular-nums tw-text-jam-muted">
                      Part {selected! + 1} · {clock(part.start)}–
                      {clock(part.end)}
                    </div>
                  </div>
                </div>
                <div className="tw-flex tw-gap-1.5">
                  {[
                    {
                      label: "Play",
                      icon: (
                        <Play size={14} fill="currentColor" strokeWidth={0} />
                      ),
                      run: () => player.play(part.start, part.end),
                    },
                    {
                      label: "Split",
                      icon: <Scissors size={14} strokeWidth={2.5} />,
                      run: () => split(selected!),
                    },
                    ...(stretches.length > 1
                      ? [
                          {
                            label: "Remove",
                            icon: <Trash2 size={14} strokeWidth={2.5} />,
                            run: () => setRemoving((r) => !r),
                          },
                        ]
                      : []),
                  ].map((b) => (
                    <button
                      key={b.label}
                      type="button"
                      onClick={b.run}
                      className={`tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-border-0 tw-px-3.5 tw-text-[13px] tw-font-extrabold ${b.label === "Remove" && removing ? "tw-bg-jam-ink tw-text-white" : "tw-bg-jam-lav tw-text-jam-ink hover:tw-bg-jam-line"}`}
                    >
                      {b.icon}
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>

              {removing ? (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                  <span className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
                    Who takes this part over?
                  </span>
                  {(
                    [
                      ["left", selected! - 1, "The part before"],
                      ["right", selected! + 1, "The part after"],
                    ] as const
                  )
                    .filter(([, k]) => k >= 0 && k < stretches.length)
                    .map(([side, k, label]) => (
                      <button
                        key={side}
                        type="button"
                        onClick={() => remove(selected!, side)}
                        className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-lav tw-py-1 tw-pl-1 tw-pr-3 tw-text-[13px] tw-font-extrabold tw-text-jam-ink hover:tw-bg-jam-line"
                      >
                        <Face
                          voice={voiceOf(stretches[k].voice)}
                          color={colorOf(stretches[k].voice)}
                          size={26}
                        />
                        {label}
                      </button>
                    ))}
                </div>
              ) : (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                  <span className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
                    Sung by
                  </span>
                  {voices.map((v) => (
                    <button
                      key={v._id}
                      type="button"
                      onClick={() => give(selected!, v._id)}
                      aria-pressed={part.voice === v._id}
                      className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-2 tw-border-solid tw-py-0.5 tw-pl-0.5 tw-pr-3 tw-text-[13px] tw-font-extrabold ${
                        part.voice === v._id
                          ? "tw-border-jam-ink tw-bg-jam-ink tw-text-white"
                          : "tw-border-jam-line tw-bg-white tw-text-jam-ink hover:tw-border-jam-soft"
                      }`}
                    >
                      <Face voice={v} color={colorOf(v._id)} size={26} />
                      {v.artistname}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="tw-rounded-[22px] tw-border-2 tw-border-dashed tw-border-jam-line tw-p-4 tw-text-center tw-text-[13px] tw-font-bold tw-text-jam-muted">
              Tap a part to hear it and choose who sings it. Drag the white
              handles to move where the voices swap.
            </div>
          )}

          {refusal && (
            <p className="tw-m-0 tw-text-center tw-text-[13px] tw-font-bold tw-text-red-500">
              {refusal}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
